import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { doc, setDoc as nativeSetDoc, getDoc as nativeGetDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { getCurrencyMeta, getDefaultCurrencyForCountry, sortCurrencyOptions } from '../../../utils/currency';
import { getPlatformFeeMinor } from '../../../utils/fees';
import { normalizeCadenceDiscountPct } from '../../../utils/calculateTotalFees';
import { formatCadenceAdjustmentLabel } from '../../../utils/cadenceAdjustments';
import { buildCadenceMirrorFields, cadencePolicyChanged, getCadenceTransitionNotice, normalizeCadencePolicy } from '../../../utils/cadenceTransition';
import { getFunctionUrl } from '../../../utils/functionURLs';
import { useAuth } from '../../../auth/AuthContext';
import ErrorMessage from '../formComponents/ErrorMessage';
import ExternalPaymentFields from './ExternalPaymentFields';
import ChildAgeLimitFields from './ChildAgeLimitFields';
import { buildExternalPaymentInstructions, parseExternalPaymentInstructions, validateExternalPaymentDetails } from '../../../utils/externalPaymentInstructions';
import SubmitButton from '../formComponents/SubmitButton';
import '../../../index.css';
const BASE_CURRENCY_OPTIONS = ['GBP', 'USD', 'AUD', 'NZD', 'CAD', 'EUR'];
const MAX_FAMILY_TIERS = 5;
const DEFAULT_NON_PAYMENT_REMOVAL_DAYS = 30;
const EXTERNAL_PAYMENT_INSTRUCTIONS_MAX_LENGTH = 1000;
const EXTERNAL_PAYMENT_DISPLAY_MODES = new Set(['price_and_instructions', 'price_only', 'instructions_only']);

function createEmptyFamilyTier(index = 0) {
  const order = Number(index) + 1;
  return {
    id: `family_tier_${Date.now()}_${order}`,
    label: `Family ${order}`,
    maxParents: 1,
    maxChildren: 2,
    baseRate: '',
    baseRateYearly: '',
    sortOrder: index
  };
}
function normalizeFamilyTierForState(rawTier = {}, index = 0) {
  const monthlyMinor = Number(rawTier.baseRate);
  const yearlyMinor = Number(rawTier.baseRateYearly);
  const monthlyMajor = Number.isFinite(monthlyMinor) ? monthlyMinor / 100 : Number.isFinite(yearlyMinor) ? yearlyMinor / 100 / 12 : null;
  const yearlyMajor = Number.isFinite(yearlyMinor) ? yearlyMinor / 100 : Number.isFinite(monthlyMinor) ? monthlyMinor / 100 * 12 : null;
  return {
    id: typeof rawTier.id === 'string' && rawTier.id.trim() ? rawTier.id.trim() : createEmptyFamilyTier(index).id,
    label: typeof rawTier.label === 'string' && rawTier.label.trim() ? rawTier.label.trim() : `Family ${index + 1}`,
    maxParents: Math.max(1, Number(rawTier.maxParents || 1)),
    maxChildren: Math.max(0, Number(rawTier.maxChildren || 0)),
    childMaxAge: rawTier.childMaxAge || null,
    childAgeCutoff: rawTier.childAgeCutoff || 'renewal',
    baseRate: monthlyMajor != null ? monthlyMajor.toString() : '',
    baseRateYearly: yearlyMajor != null ? yearlyMajor.toString() : '',
    sortOrder: Number.isFinite(Number(rawTier.sortOrder)) ? Number(rawTier.sortOrder) : index
  };
}
export default function EditSubscriptionsForm({
  isOpen,
  teamId,
  onClose,
  onSave,
  membershipMode = 'hosted_stripe',
  embedded = false,
  autoSave = false,
  dataAccess = null,
}) {
  const { getDoc, setDoc } = dataAccess || { getDoc: nativeGetDoc, setDoc: nativeSetDoc };
  const { user } = useAuth();
  const hostedPayments = membershipMode === 'hosted_stripe' || membershipMode === 'hybrid';
  const externalPayments = membershipMode !== 'hosted_stripe';
  const hybridPayments = membershipMode === 'hybrid';
  const [externalPaymentDisplayMode, setExternalPaymentDisplayMode] = useState('instructions_only');
  const [externalPaymentDetails, setExternalPaymentDetails] = useState(() => parseExternalPaymentInstructions());
  const externalPaymentInstructions = buildExternalPaymentInstructions(externalPaymentDetails);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const savedSignatureRef = useRef(null);
  const attemptedSignatureRef = useRef(null);
  const saveHandlerRef = useRef(null);
  const formRef = useRef(null);
  const savingRef = useRef(false);
  // Core pricing (we persist monthly + yearly bases; UI lets admin enter either)
  const [baseInputUnit, setBaseInputUnit] = useState('month'); // 'month' | 'year' (UI only)
  const [baseInput, setBaseInput] = useState(0); // UI entry (major units)
  const [tagPricing, setTagPricing] = useState({}); // { tagName: "12.34" } monthly major units
  const [tagPricingYearly, setTagPricingYearly] = useState({}); // { tagName: "120.00" } yearly major units
  const [tags, setTags] = useState([]);
  const [currency, setCurrency] = useState('GBP');
  const [currencyLocked, setCurrencyLocked] = useState(false);
  const [preferredCurrency, setPreferredCurrency] = useState('GBP');

  // Fees
  const [platformFeeHandling, setPlatformFeeHandling] = useState('add'); // 'add' | 'deduct'
  const [stripeFeeHandling, setStripeFeeHandling] = useState('deduct'); // 'add' | 'deduct'
  const [memberNestFeePence, setMemberNestFeePence] = useState(100); // canonical GBP fee (e.g. 100 = £1)

  // Cadence model
  const [cadenceMode, setCadenceMode] = useState('fixed'); // 'fixed' | 'member_choice'
  const [fixedUnit, setFixedUnit] = useState('year'); // when fixed: 'month' | 'year'
  const [initialCadence, setInitialCadence] = useState(null);
  const [initialCadencePolicy, setInitialCadencePolicy] = useState(null);
  const [familyTiers, setFamilyTiers] = useState([]);
  const [additionalAccountsByPricingTag, setAdditionalAccountsByPricingTag] = useState({});

  // Member's pick signed adjustments only (no enable flags): positive discounts, negative premiums.
  const [mcMonthlyDiscount, setMcMonthlyDiscount] = useState(0);
  const [mcYearlyDiscount, setMcYearlyDiscount] = useState(0);
  const [membershipYearEnabled, setMembershipYearEnabled] = useState(false);
  const [membershipYearProrateBasePrice, setMembershipYearProrateBasePrice] = useState(true);
  const [membershipYearTagProrationMode, setMembershipYearTagProrationMode] = useState('all'); // 'all' | 'none' | 'selected'
  const [membershipYearProratedTagNames, setMembershipYearProratedTagNames] = useState([]);
  const [membershipYearProrationRatio, setMembershipYearProrationRatio] = useState('day');
  const [membershipYearFullPriceWindowValue, setMembershipYearFullPriceWindowValue] = useState(0);
  const [membershipYearFullPriceWindowUnit, setMembershipYearFullPriceWindowUnit] = useState('month');
  const [membershipYearStartMonth, setMembershipYearStartMonth] = useState(1);
  const [membershipYearStartDay, setMembershipYearStartDay] = useState(1);
  const [nonPaymentRemovalDays, setNonPaymentRemovalDays] = useState(DEFAULT_NON_PAYMENT_REMOVAL_DAYS);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [billingImpactPreview, setBillingImpactPreview] = useState(null);
  const [billingApplyResult, setBillingApplyResult] = useState(null);
  const [latestBillingResult, setLatestBillingResult] = useState(null);
  const [openManualBillHandling, setOpenManualBillHandling] = useState('preserve');
  const [showAllBillingPayers, setShowAllBillingPayers] = useState(false);
  const [billingPayerRows, setBillingPayerRows] = useState([]);
  const [billingPayerSearch, setBillingPayerSearch] = useState('');
  const [billingPayerPage, setBillingPayerPage] = useState(1);
  const [billingPayerPagination, setBillingPayerPagination] = useState(null);
  const [billingPayersLoading, setBillingPayersLoading] = useState(false);

  // Helpers
  const toPence = pounds => Math.round(Number(pounds || 0) * 100);
  const fromPence = pence => Number(pence || 0) / 100;
  const formatMoney = useCallback((n, ccy = currency) => {
    const {
      symbol,
      decimals
    } = getCurrencyMeta(ccy);
    const num = Number(n || 0);
    return `${symbol}${num.toFixed(decimals)}`;
  }, [currency]);
  const formatMinorMoney = useCallback((minor, ccy = currency) => {
    const { decimals } = getCurrencyMeta(ccy);
    return formatMoney(Number(minor || 0) / Math.pow(10, decimals), ccy);
  }, [currency, formatMoney]);
  const loadBillingStatus = useCallback(async ({ changeId = '', view = 'exceptions', query = '', page = 1 } = {}) => {
    if (!user || !teamId) return null;
    const token = await user.getIdToken();
    const response = await fetch(getFunctionUrl('clubBillingPolicyChange'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ teamId, action: 'status', changeId, view, query, page, pageSize: 25 })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || 'Could not load billing-policy progress.');
    return result;
  }, [teamId, user]);
  const currencyOptions = useMemo(() => sortCurrencyOptions(BASE_CURRENCY_OPTIONS, preferredCurrency), [preferredCurrency]);

  // Derived: the monthly base from the UI input (single source of truth for saving)
  const monthlyBaseFromUI = useMemo(() => {
    if (!Number.isFinite(baseInput)) return 0;
    return baseInputUnit === 'year' ? baseInput / 12 : baseInput;
  }, [baseInput, baseInputUnit]);
  const yearlyBaseFromUI = useMemo(() => {
    if (!Number.isFinite(baseInput)) return 0;
    return baseInputUnit === 'year' ? baseInput : baseInput * 12;
  }, [baseInput, baseInputUnit]);
  const tagInputUnitLabel = baseInputUnit === 'year' ? 'yearly' : 'monthly';
  const getTagInputValue = tagName => {
    const source = baseInputUnit === 'year' ? tagPricingYearly : tagPricing;
    const raw = source[tagName];
    if (raw === undefined || raw === null || raw === '') return '';
    const value = Number(raw);
    if (!Number.isFinite(value)) return '';
    return Number(value.toFixed(2));
  };
  const handleTagPricingChange = (tagName, rawValue) => {
    if (rawValue === '') {
      setTagPricing(prev => ({
        ...prev,
        [tagName]: ''
      }));
      setTagPricingYearly(prev => ({
        ...prev,
        [tagName]: ''
      }));
      return;
    }
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      setTagPricing(prev => ({
        ...prev,
        [tagName]: ''
      }));
      setTagPricingYearly(prev => ({
        ...prev,
        [tagName]: ''
      }));
      return;
    }
    if (baseInputUnit === 'year') {
      setTagPricingYearly(prev => ({
        ...prev,
        [tagName]: parsed.toString()
      }));
      setTagPricing(prev => ({
        ...prev,
        [tagName]: (parsed / 12).toString()
      }));
    } else {
      setTagPricing(prev => ({
        ...prev,
        [tagName]: parsed.toString()
      }));
      setTagPricingYearly(prev => ({
        ...prev,
        [tagName]: (parsed * 12).toString()
      }));
    }
  };
  const familyTierInputUnit = cadenceMode === 'fixed' ? fixedUnit : baseInputUnit;
  const getFamilyTierInputValue = (tier = {}) => {
    const sourceValue = familyTierInputUnit === 'year' ? tier.baseRateYearly : tier.baseRate;
    if (sourceValue === undefined || sourceValue === null || sourceValue === '') return '';
    const parsed = Number(sourceValue);
    if (!Number.isFinite(parsed)) return '';
    return Number(parsed.toFixed(2));
  };
  const handleFamilyTierPriceChange = (tierId, rawValue) => {
    if (rawValue === '') {
      updateFamilyTier(tierId, {
        baseRate: '',
        baseRateYearly: ''
      });
      return;
    }
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      updateFamilyTier(tierId, {
        baseRate: '',
        baseRateYearly: ''
      });
      return;
    }
    if (familyTierInputUnit === 'year') {
      updateFamilyTier(tierId, {
        baseRateYearly: parsed.toString(),
        baseRate: (parsed / 12).toString()
      });
      return;
    }
    updateFamilyTier(tierId, {
      baseRate: parsed.toString(),
      baseRateYearly: (parsed * 12).toString()
    });
  };
  const addFamilyTier = () => {
    setFamilyTiers(prev => {
      if (prev.length >= MAX_FAMILY_TIERS) return prev;
      return [...prev, createEmptyFamilyTier(prev.length)];
    });
  };
  const removeFamilyTier = tierId => {
    const tier = familyTiers.find(entry => entry.id === tierId);
    if (!window.confirm(`Remove "${tier?.name || 'this family pricing tier'}" when you save membership settings?`)) return;
    setFamilyTiers(prev => prev.filter(tier => tier.id !== tierId));
  };
  const updateFamilyTier = (tierId, patch) => {
    setFamilyTiers(prev => prev.map(tier => tier.id === tierId ? {
      ...tier,
      ...patch
    } : tier));
  };

  // When "member chooses", show live previews (pre-fees; just base + signed adjustment)
  const memberChoicePreview = useMemo(() => {
    if (cadenceMode !== 'member_choice') return null;
    const monthlyBefore = monthlyBaseFromUI;
    const yearlyBefore = monthlyBaseFromUI * 12;
    const monthlyAdjustment = normalizeCadenceDiscountPct(mcMonthlyDiscount);
    const yearlyAdjustment = normalizeCadenceDiscountPct(mcYearlyDiscount);
    const monthlyAfter = monthlyBefore * (1 - monthlyAdjustment / 100);
    const yearlyAfter = yearlyBefore * (1 - yearlyAdjustment / 100);
    return {
      monthly: {
        before: monthlyBefore,
        after: monthlyAfter,
        adjustmentLabel: formatCadenceAdjustmentLabel(monthlyAdjustment, { includeZero: true })
      },
      yearly: {
        before: yearlyBefore,
        after: yearlyAfter,
        adjustmentLabel: formatCadenceAdjustmentLabel(yearlyAdjustment, { includeZero: true })
      }
    };
  }, [cadenceMode, monthlyBaseFromUI, mcMonthlyDiscount, mcYearlyDiscount]);
  const cadenceTransitionNotice = useMemo(() => getCadenceTransitionNotice({
    previous: initialCadence,
    next: { mode: cadenceMode, unit: fixedUnit },
  }), [initialCadence, cadenceMode, fixedUnit]);
  useEffect(() => {
    if (!isOpen) return;
    setSettingsLoaded(false);
    savedSignatureRef.current = null;
    attemptedSignatureRef.current = null;
    setInitialCadence(null);
    setBillingImpactPreview(null);
    setBillingApplyResult(null);
    setLatestBillingResult(null);
    setShowAllBillingPayers(false);
    setBillingPayerRows([]);
    setBillingPayerSearch('');
    setBillingPayerPage(1);
    setBillingPayerPagination(null);
    (async () => {
      try {
        // Tags (for overrides)
        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        let preferred = 'GBP';
        if (teamSnap.exists()) {
          const teamData = teamSnap.data() || {};
          const teamTags = teamData.tags || [];
          preferred = getDefaultCurrencyForCountry(teamData?.address?.country, 'GBP');
          setPreferredCurrency(preferred);
          const enriched = teamTags.flatMap(t => {
            if (t.type !== 'ageRule') return [t];
            const names = Array.isArray(t.tagNames) ? t.tagNames : [t.tagName];
            return names.filter(name => typeof name === 'string' && name.trim() !== '').map(name => ({
              ...t,
              name: name.trim(),
              category: 'Age Tags',
              type: 'ageRule'
            }));
          });
          const deduped = [];
          const seen = new Set();
          enriched.forEach(tag => {
            const key = `${tag.category || 'Uncategorised'}::${tag.name}`;
            if (seen.has(key)) return;
            seen.add(key);
            deduped.push(tag);
          });
          setTags(deduped);
        }

        // Stripe setup (pricing + cadence)
        const setupSnap = await getDoc(doc(db, `teams/${teamId}/stripe/setup`));
        const paymentDraft = externalPayments ? await getDoc(doc(db, 'teams', teamId, 'membershipPaymentSettings', 'draft')) : null;
        setExternalPaymentDetails(parseExternalPaymentInstructions(setupSnap.exists() && typeof setupSnap.data().externalPaymentInstructions === 'string' ? setupSnap.data().externalPaymentInstructions : '', paymentDraft?.exists() ? paymentDraft.data().details : undefined));
        if (setupSnap.exists()) {
          const data = setupSnap.data() || {};
          const setupCurrency = data.currency || preferred;
          setCurrency(setupCurrency);
          setCurrencyLocked(Boolean(data.currencyLocked));
          setExternalPaymentDisplayMode(EXTERNAL_PAYMENT_DISPLAY_MODES.has(data.externalPaymentDisplayMode) ? data.externalPaymentDisplayMode : 'instructions_only');
          const storedMonthly = fromPence(data.baseRate ?? 0); // per month
          const storedYearly = fromPence(data.baseRateYearly ?? Math.round(Number(data.baseRate ?? 0) * 12)); // per year

          // Cadence (load first so we know which label to show for base)
          const mode = data.cadenceMode || 'fixed';
          const storedFixedUnit = data?.fixedInterval?.unit === 'month' ? 'month' : 'year';
          setInitialCadence({ mode, unit: storedFixedUnit });
          setInitialCadencePolicy(normalizeCadencePolicy(data));
          setCadenceMode(mode);
          if (mode === 'fixed') {
            const fi = data.fixedInterval || {
              unit: 'year',
              count: 1
            };
            const u = fi.unit === 'month' ? 'month' : 'year';
            setFixedUnit(u);
            // For a fixed plan, show base as the fixed unit for a better mental model
            setBaseInputUnit(u);
            setBaseInput(u === 'year' ? storedYearly : storedMonthly);
          } else {
            const mc = data.memberChoice || {
              monthly: {
                discountPct: 0
              },
              yearly: {
                discountPct: 0
              }
            };
            setMcMonthlyDiscount(normalizeCadenceDiscountPct(mc?.monthly?.discountPct));
            setMcYearlyDiscount(normalizeCadenceDiscountPct(mc?.yearly?.discountPct));

            // For member choice, default to entering Monthly; still can switch to “enter as Yearly”
            setBaseInputUnit('month');
            setBaseInput(storedMonthly);
          }
          setPlatformFeeHandling(data.platformFeeHandling ?? 'add');
          setStripeFeeHandling(data.stripeFeeHandling ?? 'deduct');
          setMemberNestFeePence(Number.isFinite(Number(data.memberNestFeePence)) ? Number(data.memberNestFeePence) : 100);
          setMembershipYearEnabled(Boolean(data.membershipYearEnabled));
          setMembershipYearProrateBasePrice(data.membershipYearProrateBasePrice !== false);
          const loadedTagProrationMode = data.membershipYearTagProrationMode === 'all' || data.membershipYearTagProrationMode === 'none' || data.membershipYearTagProrationMode === 'selected' ? data.membershipYearTagProrationMode : data.membershipYearProrateTaggedPrices === false ? 'none' : 'all';
          setMembershipYearTagProrationMode(loadedTagProrationMode);
          setMembershipYearProratedTagNames(Array.isArray(data.membershipYearProratedTagNames) ? data.membershipYearProratedTagNames.filter(name => typeof name === 'string' && name.trim() !== '') : []);
          setMembershipYearProrationRatio(data.membershipYearProrationRatio === 'week' || data.membershipYearProrationRatio === 'month' ? data.membershipYearProrationRatio : 'day');
          const loadedFullPriceWindowUnit = data.membershipYearFullPriceWindow?.unit === 'day' ? 'day' : 'month';
          const loadedFullPriceWindowValue = Number(data.membershipYearFullPriceWindow?.value);
          const loadedFullPriceWindowMaximum = loadedFullPriceWindowUnit === 'day' ? 366 : 12;
          setMembershipYearFullPriceWindowUnit(loadedFullPriceWindowUnit);
          setMembershipYearFullPriceWindowValue(Number.isInteger(loadedFullPriceWindowValue) && loadedFullPriceWindowValue >= 0 && loadedFullPriceWindowValue <= loadedFullPriceWindowMaximum ? loadedFullPriceWindowValue : 0);
          setMembershipYearStartMonth(Number(data.membershipYearStartMonth || 1));
          setMembershipYearStartDay(Number(data.membershipYearStartDay || 1));
          const loadedNonPaymentRemovalDays = Number(data.nonPaymentRemovalDays);
          setNonPaymentRemovalDays(Number.isInteger(loadedNonPaymentRemovalDays) && loadedNonPaymentRemovalDays >= 1 && loadedNonPaymentRemovalDays <= 365 ? loadedNonPaymentRemovalDays : DEFAULT_NON_PAYMENT_REMOVAL_DAYS);
          setAdditionalAccountsByPricingTag(data.additionalAccountsByPricingTag || {});
          setFamilyTiers(Array.isArray(data.familyTiers) ? data.familyTiers.slice(0, MAX_FAMILY_TIERS).map((tier, index) => normalizeFamilyTierForState(tier, index)) : []);

          // Tag overrides (pence -> major-unit strings)
          const overridesMonthly = {};
          const overridesYearly = {};
          const monthlyMap = data.tagOverrides || {};
          const yearlyMap = data.tagOverridesYearly || {};
          const names = new Set([...Object.keys(monthlyMap), ...Object.keys(yearlyMap)]);
          for (const tagName of names) {
            const monthlyMinor = Number(monthlyMap?.[tagName]);
            const yearlyMinor = Number(yearlyMap?.[tagName]);
            const monthlyMajor = Number.isFinite(monthlyMinor) ? fromPence(monthlyMinor) : Number.isFinite(yearlyMinor) ? fromPence(yearlyMinor) / 12 : null;
            const yearlyMajor = Number.isFinite(yearlyMinor) ? fromPence(yearlyMinor) : Number.isFinite(monthlyMinor) ? fromPence(monthlyMinor) * 12 : null;
            if (monthlyMajor != null) overridesMonthly[tagName] = monthlyMajor.toFixed(2);
            if (yearlyMajor != null) overridesYearly[tagName] = yearlyMajor.toFixed(2);
          }
          setTagPricing(overridesMonthly);
          setTagPricingYearly(overridesYearly);
        } else {
          setCurrency(preferred);
          setCurrencyLocked(false);
          setMemberNestFeePence(100);
          setMembershipYearProrateBasePrice(true);
          setMembershipYearTagProrationMode('all');
          setMembershipYearProratedTagNames([]);
          setMembershipYearProrationRatio('day');
          setMembershipYearFullPriceWindowValue(0);
          setMembershipYearFullPriceWindowUnit('month');
          setNonPaymentRemovalDays(DEFAULT_NON_PAYMENT_REMOVAL_DAYS);
          setFamilyTiers([]);
          setTagPricing({});
          setTagPricingYearly({});
        }
        setSettingsLoaded(true);
        if (hostedPayments && user && !dataAccess?.platform) {
          try {
            const token = await user.getIdToken();
            const statusResponse = await fetch(getFunctionUrl('clubBillingPolicyChange'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ teamId, action: 'status' })
            });
            const latest = await statusResponse.json().catch(() => ({}));
            if (statusResponse.ok && latest?.change) setLatestBillingResult(latest);
          } catch {
            // Historical status is supplementary; the editor remains usable if it cannot load.
          }
        }
      } catch (err) {
        setError('Failed to load subscription settings.');
      }
    })();
  }, [hostedPayments, externalPayments, isOpen, teamId, user, getDoc, dataAccess]);

  const activeBillingChangeId = billingApplyResult?.changeId || latestBillingResult?.change?.changeId || '';
  const activeBillingStatus = billingApplyResult?.status || latestBillingResult?.change?.status || '';
  const hasBillingApplyResult = Boolean(billingApplyResult);
  useEffect(() => {
    if (!hostedPayments || !isOpen || !activeBillingChangeId || !['initializing', 'queued', 'processing'].includes(activeBillingStatus)) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const result = await loadBillingStatus({ changeId: activeBillingChangeId, view: 'exceptions' });
        if (cancelled || !result?.change) return;
        if (hasBillingApplyResult) {
          setBillingApplyResult(previous => ({
            ...previous,
            ...result.change,
            changeId: result.change.changeId,
            status: result.status,
            rows: result.members || [],
            pagination: result.pagination || null
          }));
        } else {
          setLatestBillingResult(result);
        }
      } catch {
        // The persistent run can be refreshed again on the next polling interval.
      }
    };
    refresh();
    const intervalId = window.setInterval(refresh, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeBillingChangeId, activeBillingStatus, hasBillingApplyResult, hostedPayments, isOpen, loadBillingStatus]);

  useEffect(() => {
    if (!isOpen || !showAllBillingPayers || !activeBillingChangeId) return undefined;
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setBillingPayersLoading(true);
      try {
        const result = await loadBillingStatus({
          changeId: activeBillingChangeId,
          view: 'all',
          query: billingPayerSearch,
          page: billingPayerPage
        });
        if (!cancelled) {
          setBillingPayerRows(result?.members || []);
          setBillingPayerPagination(result?.pagination || null);
        }
      } catch (statusError) {
        if (!cancelled) setError(statusError?.message || 'Could not load payer details.');
      } finally {
        if (!cancelled) setBillingPayersLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [activeBillingChangeId, billingPayerPage, billingPayerSearch, isOpen, loadBillingStatus, showAllBillingPayers]);

  // If the admin flips cadence mode, gently keep the base input meaningful
  useEffect(() => {
    if (cadenceMode === 'fixed') {
      // In fixed mode, keep the base input unit aligned to the fixed cadence.
      if (baseInputUnit !== fixedUnit) {
        setBaseInput(prev => {
          const n = Number(prev || 0);
          return fixedUnit === 'year' ? n * 12 : n / 12;
        });
        setBaseInputUnit(fixedUnit);
      }
      return;
    }
  }, [cadenceMode, fixedUnit, baseInputUnit]);
  const perYearPlatformFeeMinor = useMemo(() => getPlatformFeeMinor({
    currency,
    memberNestFeePence
  }), [currency, memberNestFeePence]);
  const perYearPlatformFeeLabel = useMemo(() => {
    const {
      decimals
    } = getCurrencyMeta(currency);
    const major = perYearPlatformFeeMinor / Math.pow(10, decimals);
    return formatMoney(major);
  }, [currency, perYearPlatformFeeMinor, formatMoney]);
  const configuredTagOverrideCount = useMemo(() => tags.reduce((count, tag) => {
    const name = tag?.name;
    if (typeof name !== 'string' || name.trim() === '') return count;
    const rawMonthly = tagPricing[name];
    const rawYearly = tagPricingYearly[name];
    const hasMonthly = rawMonthly !== undefined && rawMonthly !== null && rawMonthly !== '' && Number.isFinite(Number(rawMonthly));
    const hasYearly = rawYearly !== undefined && rawYearly !== null && rawYearly !== '' && Number.isFinite(Number(rawYearly));
    return hasMonthly || hasYearly ? count + 1 : count;
  }, 0), [tags, tagPricing, tagPricingYearly]);
  const configuredPricedTagNames = useMemo(() => tags.map(tag => tag?.name).filter(name => {
    if (typeof name !== 'string' || name.trim() === '') return false;
    const rawMonthly = tagPricing[name];
    const rawYearly = tagPricingYearly[name];
    const hasMonthly = rawMonthly !== undefined && rawMonthly !== null && rawMonthly !== '' && Number.isFinite(Number(rawMonthly));
    const hasYearly = rawYearly !== undefined && rawYearly !== null && rawYearly !== '' && Number.isFinite(Number(rawYearly));
    return hasMonthly || hasYearly;
  }), [tags, tagPricing, tagPricingYearly]);
  useEffect(() => {
    setMembershipYearProratedTagNames(prev => {
      const allowed = new Set(configuredPricedTagNames);
      return prev.filter(name => allowed.has(name));
    });
  }, [configuredPricedTagNames]);
  const groupTagsByCategory = arr => {
    const out = {};
    for (const tag of arr) {
      const cat = tag.category || 'Uncategorised';
      if (!out[cat]) out[cat] = [];
      out[cat].push(tag);
    }
    return out;
  };
  const settingsSignature = JSON.stringify({
    externalPaymentDisplayMode, externalPaymentDetails, baseInputUnit, baseInput,
    tagPricing, tagPricingYearly, currency, platformFeeHandling, stripeFeeHandling,
    cadenceMode, fixedUnit, familyTiers, additionalAccountsByPricingTag, mcMonthlyDiscount, mcYearlyDiscount,
    membershipYearEnabled, membershipYearProrateBasePrice, membershipYearTagProrationMode,
    membershipYearProratedTagNames, membershipYearProrationRatio,
    membershipYearFullPriceWindowValue, membershipYearFullPriceWindowUnit,
    membershipYearStartMonth, membershipYearStartDay, nonPaymentRemovalDays,
  });

  useEffect(() => {
    if (!autoSave || !settingsLoaded || !isOpen) return undefined;
    if (savedSignatureRef.current === null) {
      savedSignatureRef.current = settingsSignature;
      setAutoSaveStatus('idle');
      return undefined;
    }
    if (settingsSignature === savedSignatureRef.current) {
      setAutoSaveStatus('saved');
      attemptedSignatureRef.current = null;
      setBillingImpactPreview(null);
      return undefined;
    }
    if (submitting || billingApplyResult) return undefined;
    if (attemptedSignatureRef.current === settingsSignature) return undefined;
    setAutoSaveStatus('pending');
    const timer = window.setTimeout(() => {
      attemptedSignatureRef.current = settingsSignature;
      saveHandlerRef.current({ preventDefault() {} }, { automatic: true });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [autoSave, settingsLoaded, isOpen, settingsSignature, submitting, billingApplyResult]);

  useEffect(() => {
    if (!autoSave || !settingsLoaded || !isOpen) return undefined;
    const dirty = () => savedSignatureRef.current !== null && savedSignatureRef.current !== settingsSignature;
    const preventUnload = event => {
      if (!dirty()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const flushBeforeNavigation = async event => {
      if (!dirty() || event.defaultPrevented || event.button !== 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      event.preventDefault();
      event.stopPropagation();
      if (savingRef.current) return;
      if (formRef.current && !formRef.current.checkValidity()) {
        setAutoSaveStatus('invalid');
        formRef.current.reportValidity();
        return;
      }
      attemptedSignatureRef.current = settingsSignature;
      await saveHandlerRef.current({ preventDefault() {} }, { automatic: true });
      if (!dirty() && anchor.isConnected) anchor.click();
    };
    window.addEventListener('beforeunload', preventUnload);
    document.addEventListener('click', flushBeforeNavigation, true);
    return () => {
      window.removeEventListener('beforeunload', preventUnload);
      document.removeEventListener('click', flushBeforeNavigation, true);
    };
  }, [autoSave, settingsLoaded, isOpen, settingsSignature]);

  const handleSave = async (e, { automatic = false } = {}) => {
    e.preventDefault();
    if (savingRef.current || (autoSave && !settingsLoaded)) return;
    savingRef.current = true;
    setSubmitting(true);
    if (autoSave) setAutoSaveStatus('saving');
    setError(null);
    try {
      // Keep inactive options in an admin-only draft, separate from published instructions.
      if (externalPayments) {
        await setDoc(doc(db, 'teams', teamId, 'membershipPaymentSettings', 'draft'), { details: externalPaymentDetails });
      }
      if (formRef.current && !formRef.current.checkValidity()) {
        if (autoSave) setAutoSaveStatus('invalid');
        return;
      }
      // Build tag override maps (to minor units), preserving exact yearly values.
      const tagOverrideMap = {};
      const tagOverrideYearlyMap = {};
      tags.forEach(tag => {
        const name = tag?.name;
        if (typeof name !== 'string' || name.trim() === '') return;
        const rawMonthly = tagPricing[name];
        const rawYearly = tagPricingYearly[name];
        const monthlyMajor = Number.isFinite(Number(rawMonthly)) ? Number(rawMonthly) : Number.isFinite(Number(rawYearly)) ? Number(rawYearly) / 12 : NaN;
        const yearlyMajor = Number.isFinite(Number(rawYearly)) ? Number(rawYearly) : Number.isFinite(Number(rawMonthly)) ? Number(rawMonthly) * 12 : NaN;
        if (Number.isFinite(monthlyMajor)) {
          tagOverrideMap[name] = Math.round(monthlyMajor * 100);
        }
        if (Number.isFinite(yearlyMajor)) {
          tagOverrideYearlyMap[name] = Math.round(yearlyMajor * 100);
        }
      });

      // Persist both monthly and yearly base rates in minor units.
      const baseMonthlyPence = toPence(monthlyBaseFromUI);
      const baseYearlyPence = toPence(yearlyBaseFromUI);
      const normalizedExternalInstructions = externalPaymentInstructions.trim();
      const effectiveExternalPaymentDisplayMode = hybridPayments ? 'price_and_instructions' : externalPaymentDisplayMode;
      const externalShowsPrice = effectiveExternalPaymentDisplayMode !== 'instructions_only';
      const externalShowsInstructions = effectiveExternalPaymentDisplayMode !== 'price_only';
      if (externalPayments) {
        if (!EXTERNAL_PAYMENT_DISPLAY_MODES.has(effectiveExternalPaymentDisplayMode)) {
          throw new Error('Choose what members should see about external payment.');
        }
        const configuredBasePrice = fixedUnit === 'month' ? baseMonthlyPence : baseYearlyPence;
        if (externalShowsPrice && configuredBasePrice <= 0) {
          throw new Error('Set a positive membership price when showing the price to members.');
        }
        if (externalShowsPrice && cadenceMode === 'member_choice'
          && (normalizeCadenceDiscountPct(mcMonthlyDiscount) >= 100 || normalizeCadenceDiscountPct(mcYearlyDiscount) >= 100)) {
          throw new Error('External monthly and yearly options must remain greater than zero after adjustments.');
        }
        if (externalShowsPrice && [...Object.values(tagOverrideMap), ...Object.values(tagOverrideYearlyMap)].some(value => Number(value) <= 0)) {
          throw new Error('Every external tag-based membership price must be greater than zero.');
        }
        if (externalShowsInstructions) {
          const detailsError = validateExternalPaymentDetails(externalPaymentDetails);
          if (detailsError) throw Object.assign(new Error(detailsError), { paymentValidation: true });
        }
        if (externalShowsInstructions && !normalizedExternalInstructions) {
          throw new Error('Add payment instructions for members.');
        }
        if (externalPaymentInstructions.trim().length > EXTERNAL_PAYMENT_INSTRUCTIONS_MAX_LENGTH) {
          throw new Error(`Payment instructions must be ${EXTERNAL_PAYMENT_INSTRUCTIONS_MAX_LENGTH} characters or fewer.`);
        }
      }

      // member_choice signed adjustments only
      const memberChoice = {
        monthly: {
          discountPct: normalizeCadenceDiscountPct(mcMonthlyDiscount)
        },
        yearly: {
          discountPct: normalizeCadenceDiscountPct(mcYearlyDiscount)
        }
      };
      const fixedInterval = {
        unit: fixedUnit,
        count: 1
      };

      // Final setup doc
      const stripeSetupRef = doc(db, `teams/${teamId}/stripe/setup`);
      const existingSnap = await getDoc(stripeSetupRef);
      const existingData = existingSnap.exists() ? existingSnap.data() : {};
      const resolvedCurrency = existingData.currencyLocked && existingData.currency ? existingData.currency : currency || existingData.currency || 'GBP';
      const canonicalMemberNestFeePence = Number.isFinite(Number(existingData.memberNestFeePence)) ? Number(existingData.memberNestFeePence) : Number(memberNestFeePence || 100);
      const platformFeeMinor = getPlatformFeeMinor({
        currency: resolvedCurrency,
        memberNestFeePence: canonicalMemberNestFeePence
      });
      const validTagOverrideNames = new Set([...Object.keys(tagOverrideMap), ...Object.keys(tagOverrideYearlyMap)]);
      const resolvedMembershipYearTagProrationMode = membershipYearTagProrationMode === 'all' || membershipYearTagProrationMode === 'none' || membershipYearTagProrationMode === 'selected' ? membershipYearTagProrationMode : 'all';
      const membershipYearProratedTagsForSave = resolvedMembershipYearTagProrationMode === 'selected' ? membershipYearProratedTagNames.filter(name => validTagOverrideNames.has(name)) : [];
      const resolvedMembershipYearProrationRatio = membershipYearProrationRatio === 'week' || membershipYearProrationRatio === 'month' ? membershipYearProrationRatio : 'day';
      const resolvedMembershipYearFullPriceWindowUnit = membershipYearFullPriceWindowUnit === 'day' ? 'day' : 'month';
      const resolvedMembershipYearFullPriceWindowValue = Number(membershipYearFullPriceWindowValue);
      const membershipYearFullPriceWindowMaximum = resolvedMembershipYearFullPriceWindowUnit === 'day' ? 366 : 12;
      if (!Number.isInteger(resolvedMembershipYearFullPriceWindowValue) || resolvedMembershipYearFullPriceWindowValue < 0 || resolvedMembershipYearFullPriceWindowValue > membershipYearFullPriceWindowMaximum) {
        throw new Error(`The full-price window must be a whole number between 0 and ${membershipYearFullPriceWindowMaximum} ${resolvedMembershipYearFullPriceWindowUnit === 'day' ? 'days' : 'months'}.`);
      }
      const resolvedNonPaymentRemovalDays = Number(nonPaymentRemovalDays);
      if (!Number.isInteger(resolvedNonPaymentRemovalDays) || resolvedNonPaymentRemovalDays < 1 || resolvedNonPaymentRemovalDays > 365) {
        throw new Error('The non-payment grace period must be a whole number between 1 and 365 days.');
      }
      const legacyProrateTaggedPrices = resolvedMembershipYearTagProrationMode !== 'none';
      if (familyTiers.length > MAX_FAMILY_TIERS) {
        throw new Error(`You can add up to ${MAX_FAMILY_TIERS} family tiers.`);
      }
      const familyTiersForSave = familyTiers.map((tier, index) => {
        const label = typeof tier.label === 'string' ? tier.label.trim() : '';
        const maxParents = Math.max(1, Math.round(Number(tier.maxParents || 1)));
        const maxChildren = Math.max(0, Math.round(Number(tier.maxChildren || 0)));
        const parsedMonthlyMajor = Number(tier.baseRate);
        const parsedYearlyMajor = Number(tier.baseRateYearly);
        const resolvedMonthlyMajor = Number.isFinite(parsedMonthlyMajor) ? parsedMonthlyMajor : Number.isFinite(parsedYearlyMajor) ? parsedYearlyMajor / 12 : NaN;
        const resolvedYearlyMajor = Number.isFinite(parsedYearlyMajor) ? parsedYearlyMajor : Number.isFinite(parsedMonthlyMajor) ? parsedMonthlyMajor * 12 : NaN;
        const needsMonthlyValidation = cadenceMode === 'member_choice' || fixedUnit === 'month';
        const needsYearlyValidation = cadenceMode === 'member_choice' || fixedUnit === 'year';
        if (needsMonthlyValidation && (!Number.isFinite(resolvedMonthlyMajor) || resolvedMonthlyMajor < 0)) {
          throw new Error(`Family tier ${index + 1} needs a valid ${cadenceMode === 'member_choice' ? 'base' : 'monthly'} price.`);
        }
        if (needsYearlyValidation && (!Number.isFinite(resolvedYearlyMajor) || resolvedYearlyMajor < 0)) {
          throw new Error(`Family tier ${index + 1} needs a valid ${cadenceMode === 'member_choice' ? 'base' : 'yearly'} price.`);
        }
        if (!label) {
          throw new Error(`Family tier ${index + 1} needs a label.`);
        }
        return {
          id: typeof tier.id === 'string' && tier.id.trim() ? tier.id.trim() : `family_tier_${index + 1}`,
          label,
          maxParents,
          maxChildren,
          childMaxAge: tier.childMaxAge || null,
          childAgeCutoff: tier.childAgeCutoff || 'renewal',
          baseRate: Math.round(resolvedMonthlyMajor * 100),
          baseRateYearly: Math.round(resolvedYearlyMajor * 100),
          sortOrder: Number.isFinite(Number(tier.sortOrder)) ? Number(tier.sortOrder) : index
        };
      });
      if (externalPayments && externalShowsPrice && familyTiersForSave.some(tier => (
        cadenceMode === 'member_choice'
          ? tier.baseRate <= 0 || tier.baseRateYearly <= 0
          : fixedUnit === 'month' ? tier.baseRate <= 0 : tier.baseRateYearly <= 0
      ))) {
        throw new Error('Every external family membership price must be greater than zero.');
      }
      const existingSetupHas = key => Object.prototype.hasOwnProperty.call(existingData, key);
      const previousNonPaymentRemovalDays = Number.isInteger(Number(existingData.nonPaymentRemovalDays))
        ? Number(existingData.nonPaymentRemovalDays)
        : DEFAULT_NON_PAYMENT_REMOVAL_DAYS;
      const nonPaymentPolicyChanged = !existingSetupHas('nonPaymentRemovalDays') || previousNonPaymentRemovalDays !== resolvedNonPaymentRemovalDays;
      const nonPaymentPolicyShortened = resolvedNonPaymentRemovalDays < previousNonPaymentRemovalDays;
      const stripePayload = {
        baseRate: baseMonthlyPence,
        // monthly pence (legacy + compatibility)
        baseRateYearly: baseYearlyPence,
        // yearly pence (used for yearly cadence calculations)
        currency: resolvedCurrency,
        currencyLocked: Boolean(existingData.currencyLocked) || existingData.firstPaymentAt != null,
        memberNestFeePence: canonicalMemberNestFeePence,
        platformFeeMinor,
        platformFeeHandling,
        stripeFeeHandling,
        cadenceMode,
        membershipYearEnabled: Boolean(membershipYearEnabled),
        membershipYearProrateBasePrice: Boolean(membershipYearProrateBasePrice),
        membershipYearProrateTaggedPrices: legacyProrateTaggedPrices,
        // legacy fallback
        membershipYearTagProrationMode: resolvedMembershipYearTagProrationMode,
        membershipYearProrationRatio: resolvedMembershipYearProrationRatio,
        membershipYearFullPriceWindow: {
          value: resolvedMembershipYearFullPriceWindowValue,
          unit: resolvedMembershipYearFullPriceWindowUnit
        },
        membershipYearStartMonth: Number(membershipYearStartMonth || 1),
        membershipYearStartDay: Number(membershipYearStartDay || 1),
        nonPaymentRemovalDays: resolvedNonPaymentRemovalDays,
        mode: 'stripe'
      };
      if (externalPayments) {
        stripePayload.externalPaymentDisplayMode = effectiveExternalPaymentDisplayMode;
        stripePayload.externalPaymentInstructions = externalShowsInstructions ? normalizedExternalInstructions : '';
      }
      if (nonPaymentPolicyChanged) stripePayload.nonPaymentPolicyUpdatedAt = serverTimestamp();
      if (nonPaymentPolicyShortened) stripePayload.nonPaymentGraceResetAt = serverTimestamp();
      if (cadenceMode === 'fixed') {
        stripePayload.fixedInterval = fixedInterval;
        if (existingSetupHas('memberChoice')) {
          stripePayload.memberChoice = deleteField();
        }
      } else {
        stripePayload.memberChoice = memberChoice;
        if (existingSetupHas('fixedInterval')) {
          stripePayload.fixedInterval = deleteField();
        }
      }
      const additionalAccountsForSave = Object.fromEntries(Object.entries(additionalAccountsByPricingTag).filter(([tag]) => validTagOverrideNames.has(tag)).map(([tag, value]) => {
        const normalized = { partner: Number(value.partner || 0), child: Number(value.child || 0), nominee: Number(value.nominee || 0), childMaxAge: value.childMaxAge || null, childAgeCutoff: value.childAgeCutoff || 'renewal' };
        if (['partner', 'child', 'nominee'].some(role => !Number.isInteger(normalized[role]) || normalized[role] < 0 || normalized[role] > 20)) throw new Error('Additional account limits must be whole numbers between 0 and 20.');
        if (normalized.childMaxAge !== null && (!Number.isInteger(normalized.childMaxAge) || normalized.childMaxAge < 1 || normalized.childMaxAge > 100)) throw new Error('Child age limits must be whole numbers between 1 and 100.');
        return [tag, normalized];
      }));
      if (familyTiersForSave.some(tier => tier.childMaxAge !== null && (!Number.isInteger(tier.childMaxAge) || tier.childMaxAge < 1 || tier.childMaxAge > 100))) throw new Error('Child age limits must be whole numbers between 1 and 100.');
      const childAgeLimitsEnabled = familyTiersForSave.some(tier => tier.childMaxAge);
      const targetSetupForTransition = {
        baseRate: baseMonthlyPence,
        baseRateYearly: baseYearlyPence,
        currency: resolvedCurrency,
        platformFeeMinor,
        platformFeeHandling,
        stripeFeeHandling,
        cadenceMode,
        fixedInterval: cadenceMode === 'fixed' ? fixedInterval : null,
        memberChoice: cadenceMode === 'member_choice' ? memberChoice : null,
        tagOverrides: tagOverrideMap,
        tagOverridesYearly: tagOverrideYearlyMap,
        familyTiers: familyTiersForSave,
        additionalAccountsByPricingTag: additionalAccountsForSave,
        childAgeLimitsEnabled
      };
      const nextCadencePolicy = normalizeCadencePolicy(targetSetupForTransition);
      const cadenceIsChanging = hostedPayments && initialCadencePolicy && cadencePolicyChanged(initialCadencePolicy, nextCadencePolicy);
      if (!cadenceIsChanging) setBillingImpactPreview(null);
      const targetSetupSignature = JSON.stringify(targetSetupForTransition);
      if (cadenceIsChanging && dataAccess?.platform) throw new Error('A club admin must use the billing transition flow to change existing payment cadence.');
      if (cadenceIsChanging && billingImpactPreview?.setupSignature !== targetSetupSignature) {
        if (!user) throw new Error('You must be signed in to preview this billing change.');
        const token = await user.getIdToken(true);
        const response = await fetch(getFunctionUrl('clubBillingPolicyChange'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ teamId, action: 'preview', targetSetup: targetSetupForTransition })
        });
        const preview = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(preview?.error || 'Could not preview the effect on existing members.');
        setBillingImpactPreview({ ...preview, setupSignature: targetSetupSignature, targetSetup: targetSetupForTransition });
        if (autoSave) setAutoSaveStatus('review');
        return;
      }
      if (cadenceIsChanging && automatic) {
        setAutoSaveStatus('review');
        return;
      }
      if (Object.keys(tagOverrideMap).length > 0) {
        stripePayload.tagOverrides = tagOverrideMap;
      } else if (existingSetupHas('tagOverrides')) {
        stripePayload.tagOverrides = deleteField();
      }
      if (Object.keys(tagOverrideYearlyMap).length > 0) {
        stripePayload.tagOverridesYearly = tagOverrideYearlyMap;
      } else if (existingSetupHas('tagOverridesYearly')) {
        stripePayload.tagOverridesYearly = deleteField();
      }
      if (resolvedMembershipYearTagProrationMode === 'selected') {
        stripePayload.membershipYearProratedTagNames = membershipYearProratedTagsForSave;
      } else if (existingSetupHas('membershipYearProratedTagNames')) {
        stripePayload.membershipYearProratedTagNames = deleteField();
      }
      if (familyTiersForSave.length > 0) {
        stripePayload.familyTiers = familyTiersForSave;
      } else if (existingSetupHas('familyTiers')) {
        stripePayload.familyTiers = deleteField();
      }
      if (existingSetupHas('familyMembershipEnabled')) {
        stripePayload.familyMembershipEnabled = deleteField();
      }
      if (existingSetupHas('familyMembershipMaxMembers')) {
        stripePayload.familyMembershipMaxMembers = deleteField();
      }
      stripePayload.additionalAccountsByPricingTag = additionalAccountsForSave;
      stripePayload.childAgeLimitsEnabled = childAgeLimitsEnabled;
      let appliedBillingResult = null;
      if (cadenceIsChanging) {
        if (!user) throw new Error('You must be signed in to prepare member billing transitions.');
        const token = await user.getIdToken(true);
        const response = await fetch(getFunctionUrl('clubBillingPolicyChange'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            teamId,
            action: 'apply',
            changeId: billingImpactPreview?.changeId,
            targetSetup: targetSetupForTransition,
            openBillHandling: openManualBillHandling
          })
        });
        const transitionResult = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(transitionResult?.error || 'Member billing transitions could not be prepared, so the policy was not saved.');
        }
        appliedBillingResult = transitionResult;
        setBillingApplyResult(transitionResult);
      }
      await setDoc(stripeSetupRef, stripePayload, {
        merge: true
      });

      // Mirror concise summary on team doc (optional)
      const subscriptionsData = {
        baseRate: baseMonthlyPence,
        // monthly pence
        baseRateYearly: baseYearlyPence,
        // yearly pence
        platformFeeHandling,
        stripeFeeHandling,
        cadenceMode,
        ...buildCadenceMirrorFields({
          cadenceMode,
          fixedInterval,
          memberChoice,
          deleteValue: deleteField()
        }),
        membershipYearEnabled: Boolean(membershipYearEnabled),
        membershipYearProrateBasePrice: Boolean(membershipYearProrateBasePrice),
        membershipYearProrateTaggedPrices: legacyProrateTaggedPrices,
        // legacy fallback
        membershipYearTagProrationMode: resolvedMembershipYearTagProrationMode,
        membershipYearProratedTagNames: resolvedMembershipYearTagProrationMode === 'selected' ? membershipYearProratedTagsForSave : deleteField(),
        membershipYearProrationRatio: resolvedMembershipYearProrationRatio,
        membershipYearFullPriceWindow: {
          value: resolvedMembershipYearFullPriceWindowValue,
          unit: resolvedMembershipYearFullPriceWindowUnit
        },
        membershipYearStartMonth: Number(membershipYearStartMonth || 1),
        membershipYearStartDay: Number(membershipYearStartDay || 1),
        nonPaymentRemovalDays: resolvedNonPaymentRemovalDays,
        ...(nonPaymentPolicyChanged ? { nonPaymentPolicyUpdatedAt: serverTimestamp() } : {}),
        ...(nonPaymentPolicyShortened ? { nonPaymentGraceResetAt: serverTimestamp() } : {}),
        additionalAccountsByPricingTag: additionalAccountsForSave,
        childAgeLimitsEnabled,
        familyTiers: familyTiersForSave.length > 0 ? familyTiersForSave : deleteField(),
        familyMembershipEnabled: deleteField(),
        familyMembershipMaxMembers: deleteField(),
        currency: resolvedCurrency,
        memberNestFeePence: canonicalMemberNestFeePence,
        platformFeeMinor,
        tagOverrides: Object.keys(tagOverrideMap).length > 0 ? tagOverrideMap : deleteField(),
        tagOverridesYearly: Object.keys(tagOverrideYearlyMap).length > 0 ? tagOverrideYearlyMap : deleteField()
      };
      if (externalPayments) {
        subscriptionsData.externalPaymentDisplayMode = effectiveExternalPaymentDisplayMode;
        subscriptionsData.externalPaymentInstructions = externalShowsInstructions ? normalizedExternalInstructions : '';
      }
      await setDoc(doc(db, 'teams', teamId), {
        subscriptions: subscriptionsData
      }, {
        merge: true
      });

      // Join form pricing question: show only when there are overrides
      const hasOverrides = validTagOverrideNames.size > 0;
      const joinFormRef = doc(db, 'teams', teamId, 'joinFormConfig', 'config');
      const joinFormSnap = await getDoc(joinFormRef);
      const joinFormData = joinFormSnap.exists() ? joinFormSnap.data() : {};
      const existingQs = Array.isArray(joinFormData.questions) ? joinFormData.questions : [];
      const hasPricingQ = existingQs.some(q => q.id === 'pricingTags');
      let questions = [...existingQs];
      if (hasOverrides && !hasPricingQ) {
        questions.push({
          id: 'pricingTags',
          label: 'Which pricing options apply to you?',
          type: 'multiselect',
          required: false
        });
      } else if (!hasOverrides && hasPricingQ) {
        questions = questions.filter(q => q.id !== 'pricingTags');
      }
      const joinFormPatch = {
        questions
      };
      if (hasOverrides) joinFormPatch.enabled = true;
      await setDoc(joinFormRef, joinFormPatch, {
        merge: true
      });

      // Callback + close (show what the admin entered)
      const displayBaseStr = baseInputUnit === 'year' ? `${formatMoney(monthlyBaseFromUI)}/mo (${formatMoney(yearlyBaseFromUI)}/yr)` : `${formatMoney(monthlyBaseFromUI)}/mo (${formatMoney(yearlyBaseFromUI)}/yr)`;
      const displaySubs = [`Base: ${displayBaseStr}`, ...Object.entries(baseInputUnit === 'year' ? tagOverrideYearlyMap : tagOverrideMap).map(([tag, p]) => `${tag}: ${formatMoney(p / 100)}`), cadenceMode === 'fixed' ? `Cadence: ${fixedUnit === 'month' ? 'Monthly' : 'Yearly'}` : `Member's pick — Monthly (${formatCadenceAdjustmentLabel(memberChoice.monthly.discountPct, { includeZero: true })}), Yearly (${formatCadenceAdjustmentLabel(memberChoice.yearly.discountPct, { includeZero: true })})`, membershipYearEnabled ? `Membership year: starts ${new Date(2000, Number(membershipYearStartMonth || 1) - 1, Number(membershipYearStartDay || 1)).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long'
      })} (yearly only); ratio: ${resolvedMembershipYearProrationRatio}; full-price window: ${resolvedMembershipYearFullPriceWindowValue} ${resolvedMembershipYearFullPriceWindowUnit}${resolvedMembershipYearFullPriceWindowValue === 1 ? '' : 's'}; base proration: ${membershipYearProrateBasePrice ? 'on' : 'off'}; tag proration: ${resolvedMembershipYearTagProrationMode === 'all' ? 'all priced tags' : resolvedMembershipYearTagProrationMode === 'none' ? 'no priced tags' : `selected tags (${membershipYearProratedTagsForSave.length})`}` : 'Membership year proration: Off', familyTiersForSave.length > 0 ? `Family tiers: ${familyTiersForSave.length} configured` : 'Family tiers: Off', ...(hostedPayments ? [`Platform fee: ${platformFeeHandling === 'add' ? 'Add to member' : 'Deduct from payout'}`, `Stripe fee: ${stripeFeeHandling === 'add' ? 'Add to member' : 'Deduct from payout'}`] : [])];
      const previousMembershipYearEnabled = Boolean(existingData.membershipYearEnabled);
      const previousMembershipYearStartMonth = Number(existingData.membershipYearStartMonth || 1);
      const previousMembershipYearStartDay = Number(existingData.membershipYearStartDay || 1);
      const membershipYearReanchorSuggested = hostedPayments && Boolean(membershipYearEnabled) && (
        !previousMembershipYearEnabled
        || previousMembershipYearStartMonth !== Number(membershipYearStartMonth || 1)
        || previousMembershipYearStartDay !== Number(membershipYearStartDay || 1)
      );
      const boltOnMembershipYearReanchorSuggested = membershipYearReanchorSuggested
        && !['custom', 'purchase'].includes(existingData.boltOnRenewalDateSource)
        && existingData.boltOnMembershipYearMode
        && existingData.boltOnMembershipYearMode !== 'off';
      if (onSave) await onSave({
        displaySubs,
        membershipYearReanchorSuggested,
        boltOnMembershipYearReanchorSuggested,
      });
      if (autoSave) {
        savedSignatureRef.current = settingsSignature;
        setAutoSaveStatus('saved');
      }
      if (!appliedBillingResult && !embedded) onClose();
    } catch (err) {
      setError(err.message || 'Failed to save subscription settings.');
      if (autoSave) setAutoSaveStatus(err.paymentValidation ? 'invalid' : 'error');
    } finally {
      savingRef.current = false;
      setSubmitting(false);
    }
  };
  saveHandlerRef.current = handleSave;
  if (!isOpen) return null;
  if (autoSave && !settingsLoaded) return error
    ? <ErrorMessage message={error} />
    : <p role="status">Loading membership payment settings…</p>;
  return <>
      {!embedded && <h2 className="modal-title">{hostedPayments ? 'Edit Hosted Membership Plans & Fees' : 'Edit Membership Plans & Fees'}</h2>}
      {error && <ErrorMessage message={error} />}
      {hostedPayments && latestBillingResult && !billingApplyResult && <details className="billing-policy-result billing-policy-history">
          <summary>Latest billing-policy run: {['queued', 'processing', 'initializing'].includes(latestBillingResult.change?.status) ? 'preparing' : latestBillingResult.change?.manualReview > 0 ? 'needs attention' : 'complete'}</summary>
          <p>{latestBillingResult.change?.processed || 0} of {latestBillingResult.change?.total || 0} prepared. {latestBillingResult.change?.confirmed || 0} confirmed; {latestBillingResult.change?.manualReview || 0} need review.</p>
          {(latestBillingResult.members || []).length > 0 && <ul>
              {latestBillingResult.members.map(row => <li key={row.transitionId}>
                  <strong>{row.memberName}</strong>: {row.status === 'manual_review' ? row.failureReason || 'Stripe confirmation is outstanding.' : `${row.targetCadence?.unit === 'month' ? 'monthly' : 'yearly'} at ${formatMinorMoney(row.targetAmountMinor, row.currency || currency)} — confirmed`}
                </li>)}
            </ul>}
          {latestBillingResult.change?.manualReview > 0 && <div className="form-actions">
              <SubmitButton type="button" label={submitting ? 'Retrying…' : 'Retry Unresolved'} onClick={async () => {
                setSubmitting(true);
                setError(null);
                try {
                  const token = await user.getIdToken(true);
                  const response = await fetch(getFunctionUrl('clubBillingPolicyChange'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ teamId, action: 'retry', changeId: latestBillingResult.change.changeId, openBillHandling: latestBillingResult.change.openBillHandling })
                  });
                  const result = await response.json().catch(() => ({}));
                  if (!response.ok) throw new Error(result?.error || 'Could not retry unresolved transitions.');
                  setBillingApplyResult(result);
                  setLatestBillingResult(null);
                } catch (retryError) { setError(retryError?.message || 'Could not retry unresolved transitions.'); }
                finally { setSubmitting(false); }
              }} disabled={submitting} variant="secondary" size="sm" />
            </div>}
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => {
            setShowAllBillingPayers(value => !value);
            setBillingPayerPage(1);
          }}>{showAllBillingPayers ? 'Hide payer list' : 'View all payers'}</button>
        </details>}
      {hostedPayments && billingApplyResult && <div className="billing-policy-result" role="status">
          <strong>{['queued', 'processing', 'initializing'].includes(billingApplyResult.status) ? 'Preparing payer transitions' : billingApplyResult.manualReview > 0 ? 'Billing policy applied with exceptions' : 'Billing policy applied'}</strong>
          <p>{billingApplyResult.processed || 0} of {billingApplyResult.total || 0} prepared. {billingApplyResult.confirmed || 0} confirmed; {billingApplyResult.manualReview || 0} need review.</p>
          {['queued', 'processing', 'initializing'].includes(billingApplyResult.status) && <p className="text-muted">This continues safely in the background. You can close this window and return later.</p>}
          {(billingApplyResult.rows || []).length > 0 && <ul>
              {billingApplyResult.rows.map(row => <li key={row.transitionId}>
                  <strong>{row.memberName}</strong>: {row.status === 'manual_review' ? row.failureReason || 'Stripe confirmation is outstanding.' : `${row.targetCadence?.unit === 'month' ? 'monthly' : 'yearly'} at ${formatMinorMoney(row.targetAmountMinor, row.currency || currency)} — confirmed`}
                </li>)}
            </ul>}
          <div className="form-actions">
            {billingApplyResult.manualReview > 0 && <SubmitButton type="button" label="Retry Unresolved" onClick={async () => {
              setSubmitting(true);
              setError(null);
              try {
                const token = await user.getIdToken(true);
                const response = await fetch(getFunctionUrl('clubBillingPolicyChange'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ teamId, action: 'retry', changeId: billingApplyResult.changeId, openBillHandling: openManualBillHandling })
                });
                const result = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(result?.error || 'Could not retry unresolved transitions.');
                setBillingApplyResult(result);
              } catch (retryError) { setError(retryError?.message || 'Could not retry unresolved transitions.'); }
              finally { setSubmitting(false); }
            }} disabled={submitting} variant="secondary" size="sm" />}
            <SubmitButton type="button" label={['queued', 'processing', 'initializing'].includes(billingApplyResult.status) ? 'Close' : 'Done'} onClick={embedded ? () => setBillingApplyResult(null) : onClose} disabled={submitting} variant="primary" size="sm" />
          </div>
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => {
            setShowAllBillingPayers(value => !value);
            setBillingPayerPage(1);
          }}>{showAllBillingPayers ? 'Hide payer list' : 'View all payers'}</button>
        </div>}

      {hostedPayments && showAllBillingPayers && activeBillingChangeId && <section className="billing-policy-payers" aria-label="Billing transition payers">
          <label htmlFor="billing-policy-payer-search">Search payers</label>
          <input id="billing-policy-payer-search" className="form-input" type="search" value={billingPayerSearch} onChange={event => {
            setBillingPayerSearch(event.target.value);
            setBillingPayerPage(1);
          }} placeholder="Search by member name" />
          {billingPayersLoading ? <p className="text-muted">Loading payer details…</p> : <ul>
              {billingPayerRows.map(row => <li key={row.transitionId}>
                  <strong>{row.memberName}</strong>: {row.status === 'manual_review' ? row.failureReason || 'Needs review.' : row.status === 'queued' ? 'Waiting to be prepared.' : `${row.targetCadence?.unit === 'month' ? 'monthly' : 'yearly'} at ${formatMinorMoney(row.targetAmountMinor, row.currency || currency)} — ${row.status === 'applied' ? 'applied' : 'confirmed'}`}
                </li>)}
              {!billingPayerRows.length && <li>No matching payers.</li>}
            </ul>}
          {billingPayerPagination?.totalPages > 1 && <div className="billing-policy-pagination">
              <button type="button" className="btn btn--secondary btn--sm" disabled={billingPayerPage <= 1 || billingPayersLoading} onClick={() => setBillingPayerPage(page => Math.max(1, page - 1))}>Previous</button>
              <span>Page {billingPayerPagination.page} of {billingPayerPagination.totalPages}</span>
              <button type="button" className="btn btn--secondary btn--sm" disabled={billingPayerPage >= billingPayerPagination.totalPages || billingPayersLoading} onClick={() => setBillingPayerPage(page => page + 1)}>Next</button>
            </div>}
        </section>}

      {!billingApplyResult && <form ref={formRef} onInvalidCapture={event => {
        // Reveal invalid fields before the browser tries to focus them.
        let ancestor = event.target.parentElement;
        while (ancestor && ancestor !== formRef.current) {
          if (ancestor.tagName === 'DETAILS') ancestor.open = true;
          ancestor = ancestor.parentElement;
        }
      }} onSubmit={event => handleSave(event, { automatic: autoSave })} className={embedded ? 'modal-form membership-plans-inline-form' : 'modal-form'}>
        {autoSave && <div className="membership-autosave-status" role="status" aria-live="polite">
          <span>{!settingsLoaded ? 'Loading settings…' : autoSaveStatus === 'saving' ? 'Saving changes…' : autoSaveStatus === 'pending' ? 'Changes will save automatically…' : autoSaveStatus === 'invalid' ? (externalPayments ? 'Payment details kept. Complete the required fields to update member instructions.' : 'Complete the required fields to save your changes.') : autoSaveStatus === 'error' ? 'Changes could not be saved. Retry below.' : autoSaveStatus === 'review' ? 'Review the billing impact below to apply this change.' : autoSaveStatus === 'idle' ? 'Changes save automatically.' : 'All changes saved automatically.'}</span>
          {autoSaveStatus === 'error' && <button type="submit" className="button button-secondary button--sm" disabled={submitting}>Retry save</button>}
        </div>}
        {externalPayments && <details className="form-group external-payment-section">
          <summary className="form-subheading">{hybridPayments ? 'Offline payment options' : 'Club payment options'}</summary>
          <p className="text-muted">
            {hybridPayments ? 'Members can pay online through Member Nest or use one of the club payment options below.' : 'Choose what members see when the club collects membership payment outside Member Nest.'}
          </p>
          {!hybridPayments && <div className="form-group">
            <label htmlFor="external-payment-display-mode">Show members</label>
            <select
              id="external-payment-display-mode"
              className="form-input"
              value={externalPaymentDisplayMode}
              onChange={e => setExternalPaymentDisplayMode(e.target.value)}
              disabled={submitting}
              required
            >
              <option value="price_and_instructions">Membership price and payment instructions</option>
              <option value="price_only">Membership price only</option>
              <option value="instructions_only">Payment instructions only</option>
            </select>
          </div>}
          {(hybridPayments || externalPaymentDisplayMode !== 'price_only') && <ExternalPaymentFields
            value={externalPaymentDetails}
            onChange={setExternalPaymentDetails}
            disabled={submitting || (autoSave && !settingsLoaded)}
          />}
        </details>}
        <p className="text-muted" style={{
        marginTop: '-0.25rem'
      }}>
          {hostedPayments
            ? membershipMode === 'hybrid'
              ? 'Set the shared membership price, online fee handling, and offline payment instructions.'
              : 'Set your base pricing first. Cadence adjustments, fees, and tag rates are optional.'
            : 'Set the membership prices and schedules your club uses when collecting payment outside Member Nest. No Stripe subscription will be created.'}
        </p>

        {/* Cadence Mode FIRST */}
        <div className="form-group">
          <label>Billing Frequency *</label>
          <div className="radio-group">
            <label>
              <input type="radio" name="cadenceMode" value="fixed" checked={cadenceMode === 'fixed'} onChange={() => setCadenceMode('fixed')} disabled={submitting} />{' '}
              Fixed cadence (club decides)
            </label>
            <label>
              <input type="radio" name="cadenceMode" value="member_choice" checked={cadenceMode === 'member_choice'} onChange={() => setCadenceMode('member_choice')} disabled={submitting} />{' '}
              Member chooses monthly or yearly
            </label>
          </div>
        </div>

        {/* Fixed cadence selection */}
        {cadenceMode === 'fixed' && <div className="form-group">
            <label>Cadence for all members *</label>
            <select value={fixedUnit} onChange={e => {
          const v = e.target.value;
          // Convert displayed base to keep the “same” monthly under the hood
          // Just update the input unit and convert the shown value so the number makes sense
          if (v === 'year' && baseInputUnit === 'month') {
            setBaseInput(baseInput * 12);
          } else if (v === 'month' && baseInputUnit === 'year') {
            setBaseInput(baseInput / 12);
          }
          setFixedUnit(v);
          setBaseInputUnit(v);
        }} className="form-input" disabled={submitting}>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </div>}

        {hostedPayments && cadenceTransitionNotice && <div className="cadence-transition-notice" role="status">
          <strong>Existing subscriptions</strong>
          <span>{cadenceTransitionNotice}</span>
        </div>}

        <details className="form-group">
          <summary className="form-subheading">Family Membership Tiers</summary>
          <p className="text-muted">
            Configure separate family membership options with their own prices and parent/child limits.
          </p>
          <div className="form-group">
            <button type="button" className="button" onClick={addFamilyTier} disabled={submitting || familyTiers.length >= MAX_FAMILY_TIERS}>
              Add Family Tier
            </button>
            <p className="text-muted" style={{
            marginTop: '0.25rem'
          }}>
              Up to {MAX_FAMILY_TIERS} tiers. Individual membership remains available.
            </p>
          </div>

          {familyTiers.map((tier, index) => <div key={tier.id} className="form-group" style={{
          border: '1px solid #e5e7eb',
          padding: '0.75rem',
          borderRadius: '8px'
        }}>
              <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
                <strong>Tier {index + 1}</strong>
                <button type="button" className="button button-danger button--sm" onClick={() => removeFamilyTier(tier.id)} disabled={submitting}>
                  Remove
                </button>
              </div>

              <label style={{
            marginTop: '0.5rem'
          }}>Label</label>
              <input type="text" className="form-input" value={tier.label} onChange={e => updateFamilyTier(tier.id, {
            label: e.target.value
          })} disabled={submitting} />

              <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}>
                <div>
                  <label>
                    {cadenceMode === 'member_choice' ? `Base Price (${familyTierInputUnit === 'year' ? 'per year' : 'per month'})` : `${fixedUnit === 'year' ? 'Yearly' : 'Monthly'} Price`}
                  </label>
                  <input type="number" min="0" step="0.01" className="form-input" value={getFamilyTierInputValue(tier)} onChange={e => handleFamilyTierPriceChange(tier.id, e.target.value)} disabled={submitting} />
                </div>
              </div>
              {cadenceMode === 'fixed' && <p className="text-muted" style={{
            marginTop: '0.25rem'
          }}>
                  Family tier {hostedPayments ? 'checkout' : 'pricing'} uses a fixed {fixedUnit === 'month' ? 'monthly' : 'yearly'} schedule.
                </p>}
              {cadenceMode === 'member_choice' && <p className="text-muted" style={{
            marginTop: '0.25rem'
          }}>
                  Saved as {formatMoney(Number(tier.baseRate || 0))}/mo and {formatMoney(Number(tier.baseRateYearly || 0))}/yr. {hostedPayments ? 'Member cadence adjustments apply at checkout.' : 'These options can be shown during the joining process.'}
                </p>}

              <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}>
                <div>
                  <label>Max Parents</label>
                  <input type="number" min="1" max="20" step="1" className="form-input" value={tier.maxParents} onChange={e => updateFamilyTier(tier.id, {
                maxParents: Number(e.target.value || 1)
              })} disabled={submitting} />
                </div>
                <div>
                  <label>Max Children</label>
                  <input type="number" min="0" max="20" step="1" className="form-input" value={tier.maxChildren} onChange={e => updateFamilyTier(tier.id, {
                maxChildren: Number(e.target.value || 0)
              })} disabled={submitting} />
                </div>
              </div>
              <ChildAgeLimitFields value={tier} onChange={patch => updateFamilyTier(tier.id, patch)} disabled={submitting} />
            </div>)}
        </details>

        {/* Currency */}
        <div className="form-group">
          <label>Currency *</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} className="form-input" disabled={submitting || currencyLocked} required>
            {currencyOptions.map(ccy => <option key={ccy} value={ccy}>{ccy}</option>)}
          </select>
          {currencyLocked && <p className="text-muted" style={{
          marginTop: '0.25rem'
        }}>
              Currency is locked after the first payment.
            </p>}
        </div>

        {/* Base Rate (unit depends on above) */}
        <div className="form-group">
          <div style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'flex-end'
        }}>
            <div style={{
            flex: 1
          }}>
              <label>Base Price ({baseInputUnit === 'month' ? 'per month' : 'per year'}) *</label>
              <input type="number" step="0.01" min="0" required value={Number.isFinite(baseInput) ? baseInput : 0} onChange={e => setBaseInput(parseFloat(e.target.value))} className="form-input" disabled={submitting} />
            </div>

            {/* Only show unit switcher when member_choice (clubs may think in either unit) */}
            {cadenceMode === 'member_choice' && <div>
                <label>Enter base as</label>
                <select value={baseInputUnit} onChange={e => {
              const v = e.target.value;
              if (v === baseInputUnit) return;
              // convert displayed number but keep monthly truth the same
              if (v === 'year' && baseInputUnit === 'month') {
                setBaseInput((baseInput || 0) * 12);
              } else if (v === 'month' && baseInputUnit === 'year') {
                setBaseInput((baseInput || 0) / 12);
              }
              setBaseInputUnit(v);
            }} className="form-input" disabled={submitting}>
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>}
          </div>

          {/* Tiny helper text to make storage model clear */}
          <p className="text-muted" style={{
          marginTop: '0.25rem'
        }}>
            Saved as {formatMoney(monthlyBaseFromUI)}/mo and {formatMoney(yearlyBaseFromUI)}/yr.
          </p>
        </div>

        {/* Member's pick cadence adjustments (no checkboxes) with live preview */}
        {cadenceMode === 'member_choice' && <details className="form-group" open>
            <summary className="form-subheading">Member's Pick: Discounts / Premiums</summary>
            <p className="text-muted">Set optional percentage adjustments after tag/base selection. Use positive values for discounts and negative values for premiums.</p>

            <div className="form-group" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 120px',
          gap: '0.5rem',
          alignItems: 'center'
        }}>
              <label style={{
            margin: 0
          }}>Monthly adjustment %</label>
              <input type="number" min="-1000" max="100" step="1" value={mcMonthlyDiscount} onChange={e => setMcMonthlyDiscount(Number(e.target.value))} className="form-input" disabled={submitting} />
            </div>

            <div className="form-group" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 120px',
          gap: '0.5rem',
          alignItems: 'center'
        }}>
              <label style={{
            margin: 0
          }}>Yearly adjustment %</label>
              <input type="number" min="-1000" max="100" step="1" value={mcYearlyDiscount} onChange={e => setMcYearlyDiscount(Number(e.target.value))} className="form-input" disabled={submitting} />
            </div>

            {memberChoicePreview && <div className="text-sm text-muted" style={{
          marginTop: '0.5rem'
        }}>
                <div><strong>Preview (pre-fees):</strong></div>
                <div>Monthly ({memberChoicePreview.monthly.adjustmentLabel}): {formatMoney(memberChoicePreview.monthly.before)} → {formatMoney(memberChoicePreview.monthly.after)}</div>
                <div>Yearly ({memberChoicePreview.yearly.adjustmentLabel}): {formatMoney(memberChoicePreview.yearly.before)} → {formatMoney(memberChoicePreview.yearly.after)}</div>
              </div>}
          </details>}

        {hostedPayments && <details className="form-group">
          <summary className="form-subheading">Advanced: Fee Handling</summary>
          <p className="text-muted">Choose whether fees are added to the member total or deducted from payout.</p>
          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
          gap: '0.75rem'
        }}>
            <div>
              <label>Member Nest Fee *</label>
              <select value={platformFeeHandling} onChange={e => setPlatformFeeHandling(e.target.value)} className="form-input" required disabled={submitting}>
                <option value="add">Add {perYearPlatformFeeLabel} (pro-rated)</option>
                <option value="deduct">Deduct from payout</option>
              </select>
            </div>
            <div>
              <label>Stripe Fee *</label>
              <select value={stripeFeeHandling} onChange={e => setStripeFeeHandling(e.target.value)} className="form-input" required disabled={submitting}>
                <option value="deduct">Deduct from payout</option>
                <option value="add">Add to member total</option>
              </select>
            </div>
          </div>
        </details>}

        {hostedPayments && <details className="form-group" open>
          <summary className="form-subheading">Non-Payment</summary>
          <div className="form-group">
            <label htmlFor="non-payment-removal-days">Remove overdue members after</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 160px) auto',
              gap: '0.5rem',
              alignItems: 'center',
              maxWidth: '320px'
            }}>
              <input
                id="non-payment-removal-days"
                type="number"
                className="form-input"
                min="1"
                max="365"
                step="1"
                value={nonPaymentRemovalDays}
                onChange={e => setNonPaymentRemovalDays(e.target.value === '' ? '' : Number(e.target.value))}
                disabled={submitting}
                required
              />
              <span>days</span>
            </div>
            <p className="text-muted" style={{ marginTop: '0.25rem' }}>
              The countdown starts on the membership payment due date. The payer is warned before removal; paying clears the countdown.
            </p>
          </div>
        </details>}

        {/* Membership-Year Proration */}
        <details className="form-group">
          <summary className="form-subheading">Membership Year</summary>
          <p className="text-muted">
            Configure whether yearly base and tag prices are prorated from join date until this renewal date.
          </p>
          <div className="form-group">
            <label>
              <input type="checkbox" checked={membershipYearEnabled} onChange={e => setMembershipYearEnabled(e.target.checked)} disabled={submitting} />{' '}
              Enable membership-year proration
            </label>
          </div>

          {membershipYearEnabled && <div className="form-group">
              <label>
                <input type="checkbox" checked={membershipYearProrateBasePrice} onChange={e => setMembershipYearProrateBasePrice(e.target.checked)} disabled={submitting} />{' '}
                Prorate base membership price
              </label>
            </div>}

          {membershipYearEnabled && <div className="form-group">
              <label>Pro-rata ratio</label>
              <select className="form-input" value={membershipYearProrationRatio} onChange={e => setMembershipYearProrationRatio(e.target.value)} disabled={submitting} required>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
              <p className="text-muted" style={{
            marginTop: '0.25rem'
          }}>
                This controls how much pro-rata occurs, always rounding down to capture a full week or month of membership.
              </p>
            </div>}

          {membershipYearEnabled && <div className="form-group">
              <label>Full-price window after renewal</label>
              <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
            gap: '0.5rem'
          }}>
                <input type="number" className="form-input" min="0" max={membershipYearFullPriceWindowUnit === 'day' ? 366 : 12} step="1" value={membershipYearFullPriceWindowValue} onChange={e => setMembershipYearFullPriceWindowValue(e.target.value === '' ? '' : Number(e.target.value))} disabled={submitting} required aria-label="Full-price window duration" />
                <select className="form-input" value={membershipYearFullPriceWindowUnit} onChange={e => setMembershipYearFullPriceWindowUnit(e.target.value)} disabled={submitting} aria-label="Full-price window unit">
                  <option value="day">Days</option>
                  <option value="month">Months</option>
                </select>
              </div>
              <p className="text-muted" style={{
            marginTop: '0.25rem'
          }}>
                Eligible yearly memberships pay the full price during this window, then use the pro-rata ratio above. Use 0 to apply pro-rata immediately.
              </p>
            </div>}

          {membershipYearEnabled && <div className="form-group">
              <label>Tag price proration</label>
              <select className="form-input" value={membershipYearTagProrationMode} onChange={e => setMembershipYearTagProrationMode(e.target.value)} disabled={submitting}>
                <option value="all">Prorate all priced tags</option>
                <option value="none">Do not prorate priced tags</option>
                <option value="selected">Prorate selected priced tags only</option>
              </select>
              <p className="text-muted" style={{
            marginTop: '0.25rem'
          }}>
                Applies when a tag-based price wins over the base membership price.
              </p>
            </div>}

          {membershipYearEnabled && membershipYearTagProrationMode === 'selected' && <div className="form-group">
              <label>Prorated tags (with pricing configured)</label>
              {configuredPricedTagNames.length === 0 ? <p className="text-muted">
                  Add pricing to one or more tags below to choose specific prorated tags.
                </p> : <div style={{
            display: 'grid',
            gap: '0.35rem'
          }}>
                  {configuredPricedTagNames.map(tagName => <label key={tagName} style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center'
            }}>
                      <input type="checkbox" checked={membershipYearProratedTagNames.includes(tagName)} onChange={e => {
                setMembershipYearProratedTagNames(prev => {
                  if (e.target.checked) return [...new Set([...prev, tagName])];
                  return prev.filter(name => name !== tagName);
                });
              }} disabled={submitting} />
                      <span>{tagName}</span>
                    </label>)}
                </div>}
            </div>}

          {membershipYearEnabled && <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.5rem'
        }}>
              <div className="form-group">
                <label>Start Month</label>
                <select className="form-input" value={membershipYearStartMonth} onChange={e => setMembershipYearStartMonth(Number(e.target.value))} disabled={submitting}>
                  <option value={1}>January</option>
                  <option value={2}>February</option>
                  <option value={3}>March</option>
                  <option value={4}>April</option>
                  <option value={5}>May</option>
                  <option value={6}>June</option>
                  <option value={7}>July</option>
                  <option value={8}>August</option>
                  <option value={9}>September</option>
                  <option value={10}>October</option>
                  <option value={11}>November</option>
                  <option value={12}>December</option>
                </select>
              </div>
              <div className="form-group">
                <label>Start Day</label>
                <input type="number" className="form-input" min="1" max="31" value={membershipYearStartDay} onChange={e => setMembershipYearStartDay(Number(e.target.value || 1))} disabled={submitting} />
              </div>
            </div>}
        </details>

        {/* Tag-Based Pricing */}
        {tags.length > 0 && <details className="form-group">
            <summary className="form-subheading">
              Optional: Tag-Based Pricing{configuredTagOverrideCount > 0 ? ` (${configuredTagOverrideCount} set)` : ''}
            </summary>
            <p className="text-muted">
              Set tag rates only when specific groups pay differently. <strong>Cheapest wins</strong> before cadence adjustments.
            </p>
            <p className="text-muted" style={{
          marginTop: '0.25rem'
        }}>
              Tag prices follow your Base Price input unit above: <strong>{tagInputUnitLabel}</strong>.
            </p>
            <p className="text-muted" style={{
          marginTop: '0.25rem'
        }}>
              Monthly and yearly tag prices are both stored so yearly values stay exact.
            </p>
            {cadenceMode === 'fixed' && <p className="text-muted" style={{
          marginTop: '0.25rem'
        }}>
                Payment schedule is fixed to <strong>{fixedUnit === 'month' ? 'Monthly' : 'Yearly'}</strong>.
              </p>}
            {cadenceMode === 'member_choice' && <p className="text-muted" style={{
          marginTop: '0.25rem'
        }}>
                Members can choose monthly or yearly, so this tag rate powers both windows.
              </p>}

            {Object.entries(groupTagsByCategory(tags)).map(([category, tagsInCat]) => <div key={category}>
                <h4 className="form-subheading">{category}</h4>
                {tagsInCat.map(tag => <div key={tag.name} className="form-group">
                    <label>{tag.name} ({tagInputUnitLabel} {currency})</label>
                    <input type="number" step="0.01" min="0" value={getTagInputValue(tag.name)} onChange={e => handleTagPricingChange(tag.name, e.target.value)} className="form-input" disabled={submitting} />
                    {getTagInputValue(tag.name) !== '' && <details>
                      <summary>Additional accounts included with {tag.name}</summary>
                      <p className="text-muted">These club memberships are separate from family groups. Only active assigned accounts add platform fees to future renewals. Tags do not stack allowances.</p>
                      {['partner', 'child', 'nominee'].map(role => <label key={role} style={{ display: 'block' }}>
                        {role === 'partner' ? 'Partners' : role === 'child' ? 'Children' : 'Any nominated people'}
                        <input className="form-input" type="number" min="0" max="20" step="1" disabled={submitting} value={additionalAccountsByPricingTag[tag.name]?.[role] || 0} onChange={event => setAdditionalAccountsByPricingTag(current => ({ ...current, [tag.name]: { ...current[tag.name], [role]: Number(event.target.value) } }))} />
                      </label>)}
                      <ChildAgeLimitFields value={additionalAccountsByPricingTag[tag.name]} onChange={patch => setAdditionalAccountsByPricingTag(current => ({ ...current, [tag.name]: { ...current[tag.name], ...patch } }))} disabled={submitting} />
                    </details>}
                  </div>)}
              </div>)}
          </details>}

        {billingImpactPreview && <div className="billing-policy-impact" role="status">
            <h3>Confirm impact on existing payers</h3>
            {billingImpactPreview.previousPolicy?.mode === 'fixed' && billingImpactPreview.targetPolicy?.mode === 'fixed' && <p className="billing-policy-impact__transition">
                {billingImpactPreview.previousPolicy.fixedUnit === 'month' ? 'Monthly' : 'Yearly'} → {billingImpactPreview.targetPolicy.fixedUnit === 'month' ? 'Monthly' : 'Yearly'}
              </p>}
            <p>{billingImpactPreview.summary?.total || 0} affected: {billingImpactPreview.summary?.subscription || 0} subscriptions and {billingImpactPreview.summary?.manual || 0} manual.</p>
            <p>Current cadence: {billingImpactPreview.summary?.monthly || 0} monthly, {billingImpactPreview.summary?.yearly || 0} yearly.</p>
            {billingImpactPreview.summary?.likelyManualReview > 0 && <p className="text-warning">{billingImpactPreview.summary.likelyManualReview} Stripe schedule{billingImpactPreview.summary.likelyManualReview === 1 ? '' : 's'} may need manual review. Other payers will still be applied.</p>}
            {billingImpactPreview.summary?.openManualBills > 0 && <fieldset>
                <legend>{billingImpactPreview.summary.openManualBills} open manual bill{billingImpactPreview.summary.openManualBills === 1 ? '' : 's'}</legend>
                <label><input type="radio" name="openBillHandling" checked={openManualBillHandling === 'preserve'} onChange={() => setOpenManualBillHandling('preserve')} /> Preserve issued bills and change the following renewal</label>
                <label><input type="radio" name="openBillHandling" checked={openManualBillHandling === 'replace'} onChange={() => setOpenManualBillHandling('replace')} /> Replace unpaid bills with the new cadence and amount</label>
              </fieldset>}
          </div>}
        {submitting && billingImpactPreview && <p className="text-muted" role="status">Saving the policy and queueing payer transitions for background preparation…</p>}
        {(!autoSave || billingImpactPreview) && <div className="form-actions">
          {!embedded && <SubmitButton type="button" label="Cancel" onClick={onClose} disabled={submitting} variant="ghost" size="sm" />}
          {embedded ? (
            <button type={autoSave ? "button" : "submit"} onClick={autoSave ? handleSave : undefined} className="button button-primary" disabled={submitting}>
              {submitting ? 'Saving…' : billingImpactPreview ? 'Confirm and apply' : 'Save changes'}
            </button>
          ) : (
            <SubmitButton type="submit" label={submitting ? 'Saving…' : billingImpactPreview ? 'Confirm and Apply' : 'Save'} disabled={submitting} variant="primary" />
          )}
        </div>}
      </form>}
    </>;
}
EditSubscriptionsForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  teamId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  membershipMode: PropTypes.oneOf(['hosted_stripe', 'hybrid', 'club_gated', 'club_pay_later']),
  embedded: PropTypes.bool,
  autoSave: PropTypes.bool,
};
